import { DjotContent } from '@/components/DjotContent';
import { AsciiDocContent } from '@/components/AsciiDocContent';
import { MarkdownContent } from '@/components/MarkdownContent';
import { PlainTextContent } from '@/components/PlainTextContent';
import { parseDjot } from '@/lib/djot';
import { parseAsciiDoc } from '@/lib/asciidoc';
import { parseMarkdown } from '@/lib/markdown';
import type { ContentFormat } from '@/lib/contentFormat';

interface WikiContentProps {
  content: string;
  format: ContentFormat;
  className?: string;
}

/**
 * Dispatches to the correct renderer for a wiki article's or publication
 * section's `content`, based on a `ContentFormat` — usually the result of
 * `detectContentFormat()` (see `@/lib/contentFormat`), but overridable by
 * the reader via `ContentFormatSelect`'s dropdown when detection guesses
 * wrong (Djot and Markdown are, by design, very hard to tell apart in
 * some documents). Most callers should use `ContentBody` (which also
 * handles the `'raw'` view) rather than this directly.
 *
 * Safety net: each parser (djot/asciidoc/markdown) never throws — a parse
 * failure quietly produces an empty document instead, which would
 * otherwise render as a blank page with no indication anything went
 * wrong. If `content` is non-empty but the chosen format's parser
 * produced zero blocks (e.g. the format guess was wrong and the "real"
 * parser choked on syntax it doesn't understand), fall back to rendering
 * as plain text instead of silently showing nothing.
 */
export function WikiContent({ content, format, className }: WikiContentProps) {
  if (content.trim() && isEmptyParse(content, format)) {
    return <PlainTextContent content={content} className={className} />;
  }

  switch (format) {
    case 'djot':
      return <DjotContent content={content} className={className} />;
    case 'asciidoc':
      return <AsciiDocContent content={content} className={className} />;
    case 'markdown':
      return <MarkdownContent content={content} className={className} />;
    case 'plaintext':
      return <PlainTextContent content={content} className={className} />;
  }
}

function isEmptyParse(content: string, format: ContentFormat): boolean {
  switch (format) {
    case 'djot':
      return parseDjot(content).children.length === 0;
    case 'asciidoc':
      return parseAsciiDoc(content).children.length === 0;
    case 'markdown':
      return parseMarkdown(content).children.length === 0;
    case 'plaintext':
      return false;
  }
}
